import React, {FC, FormEventHandler, useRef} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import {useRouter} from 'next/router'

import FieldsPayStubCompanyInfo from 'components/blocks/forms/FormPayStub/FieldsPayStubCompanyInfo'
import FieldsPayStubEarningsStatement from 'components/blocks/forms/FormPayStub/FieldsPayStubEarningsStatement'
import FieldsPayStubEmployeeInfo from 'components/blocks/forms/FormPayStub/FieldsPayStubEmployeeInfo'
import FieldsPayStubStart from 'components/blocks/forms/FormPayStub/FieldsPayStubStart'
import PayStubChooseTemplate from 'components/blocks/forms/FormPayStub/PayStubChooseTemplate'
import Button from 'components/elements/Button'
import Checkbox from 'components/elements/fields/Checkbox'
import {Col, Container, Row} from 'components/elements/grid'
import MessageBlock from 'components/elements/MessageBlock'
import SpinnerDots from 'components/elements/SpinnerDots'
import {posthogTrack, klaviyoTrack, pushGTMLayer} from 'src/utils'
import {useAuthStore, useDraftStore, usePayStubStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'

import s from './FormPayStub.module.scss'
import useStepsForAnalytics from './useStepsForAnalytics'
import useChooseTemplate from '../hooks/useChooseTemplate'

const DynamicFieldsPayStubPrevYTD = dynamic(
	() => import('components/blocks/forms/FormPayStub/FieldsPayStubPrevYTD'),
	{
		loading: () => <SpinnerDots size="m" position="center" />,
	},
)
const cx = cn.bind(s)

const payStubsTemplates: IPayStubTemplates[] = [
	{
		id: 1,
		isActive: true,
	},
	{
		id: 2,
		isActive: false,
	},
	{
		id: 3,
		isActive: false,
	},
	{
		id: 4,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 5,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 6,
		isActive: false,
		theme: 'gray',
	},
]

interface IFormPayStubProps {
	className?: string
}

const FormPayStub: FC<IFormPayStubProps> = ({className}) => {
	const refsSteps = useRef<HTMLDivElement[]>([])
	const {setStepsStates} = useStepsForAnalytics(refsSteps)
	const {templates, activeTemplate, handleTemplateTheme, handleActiveTemplate} =
		useChooseTemplate<IPayStubTemplates>(payStubsTemplates)

	const router = useRouter()
	const payStubStore = usePayStubStore()
	const draftStore = useDraftStore()
	const authStore = useAuthStore()

	const [saveDraftFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof payStubStore.saveDraft>(
		payStubStore.saveDraft,
	)

	const onCheckout = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		posthogTrack('[Paytub] Checkout button clicked')
		if (!authStore.hasActiveSubscription) {
			posthogTrack(`[PAYSTUB unsubscribed] Submit button clicked`)
		}
		pushGTMLayer({event: 'paystub_checkout_btn_clicked'})
		klaviyoTrack('Checkout clicked', {
			documentType: 'paystub',
			hasActiveSubscription: authStore.hasActiveSubscription,
			subscriptionType: authStore.user?.subscription?.subscriptionType,
		})
		const draftId =
			draftStore.type === 'paystub' && draftStore.draftId ? draftStore.draftId : undefined
		const {id, theme} = activeTemplate as IPayStubTemplates
		const response = await saveDraftFlow(id, draftId, theme)()
		if (response?.data.ok) {
			draftStore.setDraft(response?.data.data.draftId, 'paystub', id, theme)

			if (!authStore.isLoggedIn) {
				return router.push('/auth/sign-up')
			}

			const canDownload = authStore.isLoggedIn && authStore.hasActiveSubscription
			router.push({
				pathname: canDownload ? '/download' : '/pricing',
			})
		}
	}

	const onIsStartedChange: FormEventHandler<HTMLDivElement> = (e) => {
		const currentTarget = e.currentTarget
		const step = currentTarget.dataset.step
		if (!step) return

		setStepsStates((prev) => {
			if (!prev[step].isStarted) {
				if (!authStore.hasActiveSubscription) {
					posthogTrack(`[PAYSTUB unsubscribed] Step ${step} started`)
				}
				posthogTrack(`[PAYSTUB] Step ${step} started`)
				return {...prev, [step]: {...prev[step], isStarted: true}}
			}
			return {...prev}
		})
	}

	return (
		<form onSubmit={onCheckout} className={cx('wrapper', className)}>
			<Container>
				<Row>
					<Col xl={{offset: 1, span: 10}}>
						<header className={cx('header', 'header--help')}>
							<h2 className={cx('header__title', 'step__title')}>0. Let’s us help!</h2>
							<div className={cx('header__description')}>
								Your pay stub in less than one minute. Immediate download! If you don&apos;t know
								where to start or need assistance, click the blue chat icon in the bottom right
								corner or email{' '}
								<Link passHref href="mailto:support@paystub.org">
									support@paystub.org
								</Link>
								.
							</div>
						</header>

						<PayStubChooseTemplate
							className="top-template"
							{...{templates, activeTemplate, handleTemplateTheme, handleActiveTemplate}}
							onChange={onIsStartedChange}
							ref={(ref) => (refsSteps.current[0] = ref as HTMLDivElement)}
							data-step="1">
							<header className={cx('header')}>
								<h2 className={cx('step__title')}>1. Choose your template</h2>
							</header>
						</PayStubChooseTemplate>

						<FieldsPayStubStart
							onChange={onIsStartedChange}
							ref={(ref) => (refsSteps.current[1] = ref as HTMLDivElement)}
							data-step="2">
							<header className={cx('header')}>
								<h2 className={cx('step__title')}>2. Start here</h2>
							</header>
						</FieldsPayStubStart>

						{payStubStore.isPrevYtd && (
							<DynamicFieldsPayStubPrevYTD>
								<header className={cx('header')}>
									<h2 className={cx('step__title')}>2.1 Enter in previous ytd here</h2>
								</header>
							</DynamicFieldsPayStubPrevYTD>
						)}

						<FieldsPayStubCompanyInfo
							onChange={onIsStartedChange}
							data-step="3"
							ref={(ref) => (refsSteps.current[2] = ref as HTMLDivElement)}>
							<header className={cx('header', 'header--company-info')}>
								<h2 className={cx('step__title')}>3. Company information</h2>
							</header>
						</FieldsPayStubCompanyInfo>

						<FieldsPayStubEmployeeInfo
							onChange={onIsStartedChange}
							data-step="4"
							ref={(ref) => (refsSteps.current[3] = ref as HTMLDivElement)}>
							<header className={cx('header')}>
								<h2 className={cx('step__title')}>4. Employee information</h2>
							</header>
						</FieldsPayStubEmployeeInfo>

						<FieldsPayStubEarningsStatement
							onChange={onIsStartedChange}
							data-step="5"
							ref={(ref) => (refsSteps.current[4] = ref as HTMLDivElement)}>
							<header className={cx('header')}>
								<h2 className={cx('step__title')}>5. Earnings statement</h2>
							</header>
						</FieldsPayStubEarningsStatement>

						<PayStubChooseTemplate
							className={cx('step--review', 'bottom-template')}
							activeTemplate={activeTemplate}
							templates={templates}
							handleTemplateTheme={handleTemplateTheme}
							handleActiveTemplate={handleActiveTemplate}
							onChange={onIsStartedChange}
							data-step="6"
							ref={(ref) => (refsSteps.current[5] = ref as HTMLDivElement)}>
							<header className={cx('header')}>
								<h2 className={cx('step__title')}>6. Review your final paystub</h2>
							</header>
						</PayStubChooseTemplate>
					</Col>
				</Row>
			</Container>
			<Container>
				<Row>
					<Col md={{offset: 2, span: 8}} lg={{offset: 3, span: 6}} xl={{offset: 3, span: 6}}>
						<div
							className={cx('step', 'step--finish', 'finish')}
							data-step="finish"
							ref={(ref) => (refsSteps.current[6] = ref as HTMLDivElement)}>
							<div className={cx('finish__content')}>
								<div className={cx('finish__fields')}>
									<Checkbox
										classNameWrapper={cx('finish--privacy')}
										required
										name="privacy"
										value={undefined}
										label="I agree that I will not use this site for any illegal purposes"
									/>
									<Checkbox
										required
										value={undefined}
										name="terms"
										label={
											<span>
												Please accept our <Link href="/terms">Terms of Use</Link> and
												<Link href="/privacy-policy">Privacy Policy</Link>{' '}
											</span>
										}
									/>
								</div>
								{(errorMessage || payStubStore.errorMessage) && (
									<div className={cx('finish__error')}>
										<MessageBlock
											type="error"
											message={errorMessage || payStubStore.errorMessage}
										/>
									</div>
								)}
								<div className={cx('finish__buttons')}>
									<Button
										disabled={!!payStubStore.errorMessage}
										type="submit"
										isLoading={isLoading}
										size="big"
										data-testid="checkout">
										Checkout
									</Button>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</Container>
		</form>
	)
}

FormPayStub.displayName = 'FormPayStub'

export default observer(FormPayStub)
